import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import type { PremiumCapability } from '../components/premium/PremiumFeatureGate';

export type PremiumEntitlementState = {
  loading: boolean;
  premiumEnabled: boolean;
  isPremium: boolean;
  status: string;
  capabilities: Record<PremiumCapability, boolean>;
  source: string | null;
};

const EMPTY_CAPS: Record<PremiumCapability, boolean> = {
  bilt_premium: false,
  premium_maintenance: false,
  premium_ai: false,
  premium_reports: false,
  premium_documents: false,
  premium_presentation: false,
};

export function usePremiumEntitlements(enabled = true): PremiumEntitlementState & {
  refresh: () => Promise<void>;
  hasCapability: (cap: PremiumCapability) => boolean;
} {
  const [state, setState] = useState<PremiumEntitlementState>({
    loading: enabled,
    premiumEnabled: false,
    isPremium: false,
    status: 'none',
    capabilities: { ...EMPTY_CAPS },
    source: null,
  });

  const refresh = useCallback(async () => {
    if (!enabled) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const data = await apiService.getPremiumStatus();
      const caps = (data.capabilities as Record<string, boolean> | undefined) ?? {};
      setState({
        loading: false,
        premiumEnabled: Boolean(data.premium_enabled),
        isPremium: Boolean(data.is_premium),
        status: String(data.status ?? 'none'),
        capabilities: {
          bilt_premium: Boolean(caps.bilt_premium),
          premium_maintenance: Boolean(caps.premium_maintenance),
          premium_ai: Boolean(caps.premium_ai),
          premium_reports: Boolean(caps.premium_reports),
          premium_documents: Boolean(caps.premium_documents),
          premium_presentation: Boolean(caps.premium_presentation),
        },
        source: data.source != null ? String(data.source) : null,
      });
    } catch {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const hasCapability = useCallback(
    (cap: PremiumCapability) => state.capabilities[cap] === true,
    [state.capabilities]
  );

  return { ...state, refresh, hasCapability };
}

export default usePremiumEntitlements;
